import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import api, { API_ENDPOINTS } from '../config/api';
import {
  Lock,
  TrendingUp,
  Clock,
  CheckCircle,
  X,
  Wallet,
  RefreshCw,
  ExternalLink,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { formatWalletError, dismissWalletToasts } from '../utils/walletErrors';
import { formatEther, parseEther, Contract } from 'ethers';
import { isInsufficientNativeGasError, requestGasDrip } from '../config/gasDrip';
import {
  getLOBTokenBalance,
  approveLOBTokens,
  checkTokenApproval,
  stakeTokens,
  stakeLockedTokens,
  unstakeTokens,
  claimRewards,
  claimAllRewards,
  claimRewardAmount,
  claimStakeReward,
  getPoolInfo,
  getUserStakingDetails,
  getUserStakeIds,
  getStakeDetails,
  getAllStakers,
  getAllActiveStakes,
  calculateEstimatedRewards
} from '../config/StakingIntegration';
import { WorkLobStaking_address, WorkLobStaking_abi } from '../config/Constants';

const LOCK_DAYS_OPTIONS = [7, 14, 30, 60, 90, 365];

const StakingPage = () => {
  const { user } = useAuth();
  const {
    isConnected,
    walletAddress,
    provider,
    signer,
    connectWallet
  } = useWeb3();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('overview');
  const [stakingType, setStakingType] = useState('normal'); // 'normal' or 'locked'
  const [stakeAmount, setStakeAmount] = useState('');
  const [selectedLockDays, setSelectedLockDays] = useState(7);
  const [isStaking, setIsStaking] = useState(false);
  const [lobTokenBalance, setLobTokenBalance] = useState('0');
  const [poolInfo, setPoolInfo] = useState(null);
  const [userStakes, setUserStakes] = useState([]);
  const [allStakes, setAllStakes] = useState([]);
  const [userStats, setUserStats] = useState({
    totalStaked: '0',
    totalLocked: '0',
    claimableRewards: '0'
  });
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'ended'
  const [filterType, setFilterType] = useState('all'); // 'all', 'normal', 'locked'
  const [currentPage, setCurrentPage] = useState(1);
  const [allStakesPage, setAllStakesPage] = useState(1);
  const [myStakesPage, setMyStakesPage] = useState(1);
  const rowsPerPage = 10;
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimingStakeId, setClaimingStakeId] = useState(null);
  const [showUnstakeModal, setShowUnstakeModal] = useState(false);
  const [unstakeStakeId, setUnstakeStakeId] = useState(null);
  const [unstakeAmount, setUnstakeAmount] = useState(null);

  // Fetch pool info
  const { data: poolInfoData, refetch: refetchPoolInfo } = useQuery(
    'poolInfo',
    async () => {
      if (!provider) return null;
      return await getPoolInfo(provider);
    },
    {
      enabled: !!provider,
      refetchInterval: 30000
    }
  );

  // Fetch user stakes
  const { data: userStakesData, refetch: refetchUserStakes } = useQuery(
    ['userStakes', walletAddress],
    async () => {
      if (!walletAddress || !provider) return null;
      
      const [details, stakeIds] = await Promise.all([
        getUserStakingDetails(provider, walletAddress),
        getUserStakeIds(provider, walletAddress)
      ]);

      const stakes = [];
      let totalClaimableRewards = 0;
      
      for (const stakeId of stakeIds) {
        try {
          const stake = await getStakeDetails(provider, stakeId);
          if (stake.isActive) {
            // Get individual stake reward
            try {
              const stakingContract = new Contract(
                WorkLobStaking_address,
                WorkLobStaking_abi,
                provider
              );
              const reward = await stakingContract.getStakeReward(stakeId);
              const rewardAmount = parseFloat(formatEther(reward));
              stake.rewardsEarned = formatEther(reward);
              totalClaimableRewards += rewardAmount;
            } catch (error) {
              console.error(`Error getting reward for stake ${stakeId}:`, error);
              stake.rewardsEarned = '0';
            }
            stakes.push(stake);
          }
        } catch (error) {
          console.error(`Error fetching stake ${stakeId}:`, error);
        }
      }

      // Update claimable rewards to be the sum of all individual stake rewards
      // This ensures accuracy when individual stakes are claimed
      return { 
        details: {
          ...details,
          claimableRewards: totalClaimableRewards.toFixed(4)
        }, 
        stakes 
      };
    },
    {
      enabled: !!walletAddress && !!provider,
      refetchInterval: 30000
    }
  );

  // Fetch all stakes for overview
  const allStakesSource = provider ? 'chain' : 'api';
  const { data: allStakesData, refetch: refetchAllStakes, isFetching: isFetchingAllStakes } = useQuery(
    ['allStakes', allStakesSource, currentPage, filterStatus],
    async () => {
      if (provider) {
        // Fetch individual stakes from blockchain
        const stakes = await getAllActiveStakes(provider);
        return { stakes: stakes, total: stakes.length };
      } else {
        // Fallback to API
        const response = await api.get(API_ENDPOINTS.STAKING.ALL_STAKES, {
          params: {
            page: currentPage,
            limit: 50,
            status: filterStatus === 'all' ? undefined : filterStatus
          }
        });
        return response.data;
      }
    },
    {
      enabled: true,
      refetchInterval: 30000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      staleTime: 0,
      keepPreviousData: false
    }
  );

  // Fetch LOB token balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!isConnected || !walletAddress) {
        setLobTokenBalance('0');
        return;
      }

      // Use provider if available, otherwise use signer
      const balanceProvider = provider || signer;
      if (!balanceProvider) {
        setLobTokenBalance('0');
        return;
      }

      try {
        const balance = await getLOBTokenBalance(balanceProvider, walletAddress);
        setLobTokenBalance(balance || '0');
      } catch (error) {
        console.error('Error fetching LOB balance:', error);
        setLobTokenBalance('0');
      }
    };
    
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, [isConnected, walletAddress, provider, signer]);

  // Update state from queries
  useEffect(() => {
    if (poolInfoData) {
      setPoolInfo(poolInfoData);
    }
  }, [poolInfoData]);

  useEffect(() => {
    if (userStakesData) {
      // Use the calculated claimable rewards from individual stakes (more accurate)
      setUserStats({
        ...userStakesData.details,
        claimableRewards: userStakesData.details.claimableRewards || '0'
      });
      setUserStakes(userStakesData.stakes);
    }
  }, [userStakesData]);

  // Update allStakes when data changes
  useEffect(() => {
    if (isFetchingAllStakes) {
      setAllStakes([]);
      return;
    }
    if (allStakesData && allStakesData.stakes) {
      // Data is already in the correct format from getAllActiveStakes
      setAllStakes(allStakesData.stakes);
    } else {
      setAllStakes([]);
    }
  }, [allStakesData, isFetchingAllStakes]);

  // Calculate estimated rewards
  const estimatedRewards = useMemo(() => {
    const amount = parseFloat(stakeAmount) || 0;
    const rewardRate = parseFloat(poolInfo?.currentRewardRate) || 0;
    const totalStaked = parseFloat(poolInfo?.totalStaked) || 0;

    return {
      day1: calculateEstimatedRewards(amount, rewardRate, totalStaked, 1),
      day7: calculateEstimatedRewards(amount, rewardRate, totalStaked, 7),
      day30: calculateEstimatedRewards(amount, rewardRate, totalStaked, 30),
      day365: calculateEstimatedRewards(amount, rewardRate, totalStaked, 365)
    };
  }, [stakeAmount, poolInfo]);

  // Resolve signer helper
  const resolveSigner = async () => {
    if (signer) {
      // If signer is a Promise, await it
      if (typeof signer.then === 'function') {
        return await signer;
      }
      // If signer has getAddress, it's already a signer
      if (typeof signer.getAddress === 'function') {
        return signer;
      }
    }
    // If we have a provider, get signer from it
    if (provider && typeof provider.getSigner === 'function') {
      return await provider.getSigner();
    }
    throw new Error('Unable to resolve signer');
  };

  // Handle stake
  const handleStake = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!stakeAmount || parseFloat(stakeAmount) < 1) {
      toast.error('Minimum staking amount is 1 LOB');
      return;
    }

    const amount = parseFloat(stakeAmount);
    const balance = parseFloat(lobTokenBalance);

    if (amount > balance) {
      toast.error('Insufficient LOB token balance');
      return;
    }

    setIsStaking(true);
    try {
      // Resolve signer
      const resolvedSigner = await resolveSigner();
      const userWallet = await resolvedSigner.getAddress();
      
      // Check and approve tokens (always check for both normal and locked)
      const approved = await checkTokenApproval(resolvedSigner, stakeAmount);
      if (!approved) {
        toast.loading('Approving tokens...');
        try {
          try {
            await approveLOBTokens(resolvedSigner, stakeAmount);
          } catch (e) {
            if (!isInsufficientNativeGasError(e)) throw e;
            await requestGasDrip(userWallet);
            await approveLOBTokens(resolvedSigner, stakeAmount);
          }
          toast.dismiss();
          toast.success('Tokens approved');
          // Wait a moment for the approval to be processed on-chain
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (approvalError) {
          toast.dismiss();
          console.error('Approval error:', approvalError);
          throw new Error('Token approval failed. Please try again.');
        }
      }

      // Stake tokens
      toast.loading('Staking tokens...');
      let result;
      if (stakingType === 'normal') {
        try {
          result = await stakeTokens(resolvedSigner, stakeAmount);
        } catch (e) {
          if (!isInsufficientNativeGasError(e)) throw e;
          await requestGasDrip(userWallet);
          result = await stakeTokens(resolvedSigner, stakeAmount);
        }
      } else {
        // For locked staking, ensure lockDays is valid
        if (!selectedLockDays || selectedLockDays <= 0) {
          throw new Error('Please select a valid lock period');
        }
        try {
          result = await stakeLockedTokens(resolvedSigner, stakeAmount, selectedLockDays);
        } catch (e) {
          if (!isInsufficientNativeGasError(e)) throw e;
          await requestGasDrip(userWallet);
          result = await stakeLockedTokens(resolvedSigner, stakeAmount, selectedLockDays);
        }
      }

      toast.dismiss();
      toast.success('Tokens staked successfully!');
      
      // Clear input immediately after successful staking
      setStakeAmount('');
      setSelectedLockDays(null);
      
      // Record stake in backend (don't block on this)
      try {
        await api.post(API_ENDPOINTS.STAKING.RECORD_STAKE, {
          stakeId: result.stakeId,
          amount: parseFloat(stakeAmount),
          isLocked: stakingType === 'locked',
          lockDays: stakingType === 'locked' ? selectedLockDays : null,
          txHash: result.txHash
        });
      } catch (backendError) {
        console.error('Error recording stake in backend:', backendError);
        // Don't show error to user as the staking already succeeded on blockchain
      }
      
      queryClient.invalidateQueries('userStakes');
      queryClient.invalidateQueries('allStakes');
      queryClient.invalidateQueries('poolInfo');
      
      // Refresh balance
      const newBalance = await getLOBTokenBalance(provider || signer, walletAddress);
      setLobTokenBalance(newBalance || '0');
      
      // Refresh the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      console.error('Staking error:', error);
      toast.error(formatWalletError(error, 'Failed to stake tokens'));
    } finally {
      dismissWalletToasts();
      setIsStaking(false);
    }
  };

  // Handle unstake confirmation modal
  const handleUnstakeClick = (stakeId, amount) => {
    setUnstakeStakeId(stakeId);
    setUnstakeAmount(amount);
    setShowUnstakeModal(true);
  };

  // Handle unstake
  const handleUnstake = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet');
      setShowUnstakeModal(false);
      return;
    }

    if (!unstakeStakeId) {
      setShowUnstakeModal(false);
      return;
    }

    setIsStaking(true);
    try {
      const resolvedSigner = await resolveSigner();
      const userWallet = await resolvedSigner.getAddress();
      toast.loading('Unstaking tokens...');
      let result;
      try {
        result = await unstakeTokens(resolvedSigner, unstakeStakeId);
      } catch (e) {
        if (!isInsufficientNativeGasError(e)) throw e;
        await requestGasDrip(userWallet);
        result = await unstakeTokens(resolvedSigner, unstakeStakeId);
      }

      // Record unstake in backend
      try {
        await api.post(API_ENDPOINTS.STAKING.RECORD_UNSTAKE, {
          stakeId: unstakeStakeId,
          txHash: result.txHash
        });
      } catch (backendError) {
        // Non-fatal: unstake already succeeded on-chain; backend may be down/misrouted.
        console.error('Error recording unstake in backend:', backendError);
      }

      toast.dismiss();
      toast.success('Tokens unstaked successfully!');
      
      setShowUnstakeModal(false);
      setUnstakeStakeId(null);
      setUnstakeAmount(null);
      
      queryClient.invalidateQueries('userStakes');
      queryClient.invalidateQueries('allStakes');
      
      // Refresh balance
      const newBalance = await getLOBTokenBalance(provider || signer, walletAddress);
      setLobTokenBalance(newBalance || '0');
      
      // Refresh the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      console.error('Unstaking error:', error);
      toast.error(formatWalletError(error, 'Failed to unstake tokens'));
    } finally {
      dismissWalletToasts();
      setIsStaking(false);
    }
  };

  // Handle claim reward
  const handleClaimReward = async (stakeId = null) => {
    if (!isConnected) {
      toast.error('Please connect your wallet');
      return;
    }

    setIsClaiming(true);
    setClaimingStakeId(stakeId);
    try {
      const resolvedSigner = await resolveSigner();
      const userWallet = await resolvedSigner.getAddress();
      toast.loading('Claiming rewards...');
      
      let result;
      let rewardAmount;
      
      if (stakeId) {
        // Claim individual stake reward using the new function that resets the counter
        // Get the stake-specific reward amount first to record it
        const stakingContract = new Contract(WorkLobStaking_address, WorkLobStaking_abi, provider);
        const stakeRewardWei = await stakingContract.getStakeReward(stakeId);
        rewardAmount = parseFloat(formatEther(stakeRewardWei));
        
        if (rewardAmount <= 0) {
          toast.dismiss();
          toast.error('No rewards available for this stake');
          setIsClaiming(false);
          setClaimingStakeId(null);
          return;
        }
        
        // Use claimStakeReward which resets the stake's reward counter
        try {
          result = await claimStakeReward(resolvedSigner, stakeId);
        } catch (e) {
          if (!isInsufficientNativeGasError(e)) throw e;
          await requestGasDrip(userWallet);
          result = await claimStakeReward(resolvedSigner, stakeId);
        }
      } else {
        // Claim all rewards using the new function that resets all stake counters
        try {
          result = await claimAllRewards(resolvedSigner);
        } catch (e) {
          if (!isInsufficientNativeGasError(e)) throw e;
          await requestGasDrip(userWallet);
          result = await claimAllRewards(resolvedSigner);
        }
        rewardAmount = parseFloat(userStats.claimableRewards);
      }

      // Record claim in backend
      await api.post(API_ENDPOINTS.STAKING.RECORD_CLAIM, {
        amount: rewardAmount,
        txHash: result.txHash,
        stakeId: stakeId || null
      });

      toast.dismiss();
      toast.success('Rewards claimed successfully!');
      
      queryClient.invalidateQueries('userStakes');
      
      // Refresh balance
      const newBalance = await getLOBTokenBalance(provider || signer, walletAddress);
      setLobTokenBalance(newBalance || '0');
      
      // Refresh the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      console.error('Claim error:', error);
      toast.error(formatWalletError(error, 'Failed to claim rewards'));
    } finally {
      dismissWalletToasts();
      setIsClaiming(false);
      setClaimingStakeId(null);
    }
  };

  // Set max amount
  const handleMaxAmount = () => {
    setStakeAmount(lobTokenBalance);
  };

  // Filter stakes
  const filteredStakes = useMemo(() => {
    const stakes = activeTab === 'overview' ? allStakes : userStakes;
    
    return stakes.filter(stake => {
      if (filterStatus === 'active' && !stake.isActive) return false;
      if (filterStatus === 'ended' && stake.isActive) return false;
      if (filterType === 'normal' && stake.isLocked) return false;
      if (filterType === 'locked' && !stake.isLocked) return false;
      return true;
    });
  }, [activeTab, allStakes, userStakes, filterStatus, filterType]);

  // Pagination calculations
  const allStakesFiltered = useMemo(() => {
    return allStakes.filter(stake => {
      if (filterStatus === 'active' && !stake.isActive) return false;
      if (filterStatus === 'ended' && stake.isActive) return false;
      if (filterType === 'normal' && stake.isLocked) return false;
      if (filterType === 'locked' && !stake.isLocked) return false;
      return true;
    });
  }, [allStakes, filterStatus, filterType]);

  const myStakesFiltered = useMemo(() => {
    return userStakes.filter(stake => {
      if (filterStatus === 'active' && !stake.isActive) return false;
      if (filterStatus === 'ended' && stake.isActive) return false;
      if (filterType === 'normal' && stake.isLocked) return false;
      if (filterType === 'locked' && !stake.isLocked) return false;
      return true;
    });
  }, [userStakes, filterStatus, filterType]);

  // Paginated data
  const allStakesPaginated = useMemo(() => {
    const startIndex = (allStakesPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return allStakesFiltered.slice(startIndex, endIndex);
  }, [allStakesFiltered, allStakesPage]);

  const myStakesPaginated = useMemo(() => {
    const startIndex = (myStakesPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return myStakesFiltered.slice(startIndex, endIndex);
  }, [myStakesFiltered, myStakesPage]);

  // Total pages
  const allStakesTotalPages = Math.ceil(allStakesFiltered.length / rowsPerPage);
  const myStakesTotalPages = Math.ceil(myStakesFiltered.length / rowsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setAllStakesPage(1);
  }, [filterStatus, filterType]);

  useEffect(() => {
    setMyStakesPage(1);
  }, [filterStatus, filterType]);

  // Check if stake is locked and not unlocked
  const isStakeLocked = (stake) => {
    if (!stake.isLocked) return false;
    const unlockTime = parseInt(stake.unlockTime);
    return Date.now() < unlockTime * 1000;
  };

  // Get time until unlock
  const getTimeUntilUnlock = (stake) => {
    if (!stake.isLocked) return null;
    const unlockTime = parseInt(stake.unlockTime) * 1000;
    if (Date.now() >= unlockTime) return null;
    return formatDistanceToNow(new Date(unlockTime), { addSuffix: true });
  };

  if (!isConnected && activeTab === 'staking') {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <Wallet className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Connect Wallet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please connect your wallet to view your staking statistics and manage your stakes.
            </p>
            <button
              onClick={connectWallet}
              className="btn-primary"
            >
              Connect Wallet
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-heading mb-2">Staking</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Stake your LOB tokens to earn rewards
          </p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-b-2 border-accent text-accent'
                : 'text-gray-600 dark:text-gray-400 hover:text-heading'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('staking')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'staking'
                ? 'border-b-2 border-accent text-accent'
                : 'text-gray-600 dark:text-gray-400 hover:text-heading'
            }`}
          >
            My Staking
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Staked LOB
                  </h3>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-2xl font-bold text-heading">
                  {poolInfo ? parseFloat(poolInfo.totalStaked || 0).toLocaleString() : '0'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Pool Balance: {poolInfo ? parseFloat(poolInfo.totalPoolBalance || 0).toLocaleString() : '0'} LOB
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Reward Rate (per second)
                  </h3>
                  <Lock className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-heading">
                  {poolInfo ? parseFloat(poolInfo.currentRewardRate || 0).toFixed(4) : '0.0000'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  APY: {poolInfo && parseFloat(poolInfo.totalStaked || 0) > 0 && parseFloat(poolInfo.apy || 0) > 0
                    ? `${parseFloat(poolInfo.apy || 0).toFixed(2)}%` 
                    : poolInfo && parseFloat(poolInfo.currentRewardRate || 0) > 0
                    ? 'Stake to see APY'
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Your Balance
                  </h3>
                  <Wallet className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-heading">
                  {parseFloat(lobTokenBalance).toLocaleString()} LOB
                </p>
              </div>
            </div>

            {/* Staking Input Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-heading mb-4">Stake LOB Tokens</h2>
              
              {!isConnected ? (
                <div className="text-center py-8">
                  <Wallet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Connect your wallet to stake tokens
                  </p>
                  <button onClick={connectWallet} className="btn-primary">
                    Connect Wallet
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Staking Type Toggle */}
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Staking Type:
                    </label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setStakingType('normal')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          stakingType === 'normal'
                            ? 'bg-accent text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Normal
                      </button>
                      <button
                        onClick={() => setStakingType('locked')}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          stakingType === 'locked'
                            ? 'bg-accent text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Locked
                      </button>
                    </div>
                  </div>

                  {/* Lock Days Selection (for locked staking) */}
                  {stakingType === 'locked' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Lock Period:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {LOCK_DAYS_OPTIONS.map((days) => (
                          <button
                            key={days}
                            onClick={() => setSelectedLockDays(days)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              selectedLockDays === days
                                ? 'bg-accent text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            {days} {days === 1 ? 'Day' : 'Days'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Amount Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Amount to Stake:
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        min="1"
                        step="0.0001"
                        value={stakeAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty input for user to type
                          if (value === '') {
                            setStakeAmount('');
                            return;
                          }
                          // Allow decimal input while typing (e.g., "0.", "0.5")
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && (numValue >= 0 || value.startsWith('0.') || value === '0')) {
                            setStakeAmount(value);
                          }
                        }}
                        onBlur={(e) => {
                          // Ensure minimum of 1 on blur
                          const value = parseFloat(e.target.value);
                          if (value && value < 1) {
                            setStakeAmount('1');
                            toast.error('Minimum staking amount is 1 LOB');
                          }
                        }}
                        placeholder="1.0"
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      <button
                        onClick={handleMaxAmount}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Max
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Available: {parseFloat(lobTokenBalance).toLocaleString()} LOB
                    </p>
                  </div>

                  {/* Estimated Rewards */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estimated Staking Rewards:
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">1 Day</p>
                        <p className="text-sm font-semibold text-heading">
                          {parseFloat(estimatedRewards.day1 || 0).toFixed(4)} LOB
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">7 Days</p>
                        <p className="text-sm font-semibold text-heading">
                          {parseFloat(estimatedRewards.day7 || 0).toFixed(4)} LOB
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">30 Days</p>
                        <p className="text-sm font-semibold text-heading">
                          {parseFloat(estimatedRewards.day30 || 0).toFixed(4)} LOB
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">365 Days</p>
                        <p className="text-sm font-semibold text-heading">
                          {parseFloat(estimatedRewards.day365 || 0).toFixed(4)} LOB
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stake Button */}
                  <button
                    onClick={handleStake}
                    disabled={isStaking || !stakeAmount || parseFloat(stakeAmount) < 1}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isStaking ? 'Staking...' : 'Stake Tokens'}
                  </button>
                </div>
              )}
            </div>

            {/* All Stakes Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-heading">All Stakes</h2>
                  <div className="flex items-center space-x-2">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="ended">Ended</option>
                    </select>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="normal">Normal</option>
                      <option value="locked">Locked</option>
                    </select>
                  </div>
                </div>
              </div>

              {!isConnected ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Connect wallet to view staking
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Staked Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Rewards Earned
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {allStakesFiltered.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                            No stakes found
                          </td>
                        </tr>
                      ) : (
                        allStakesPaginated.map((stake, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {stake.address || stake.walletAddress || stake.staker
                                ? `${(stake.address || stake.walletAddress || stake.staker).slice(0, 6)}...${(stake.address || stake.walletAddress || stake.staker).slice(-4)}`
                                : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {parseFloat(stake.stakedAmount || stake.amount || 0).toLocaleString()} LOB
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {parseFloat(stake.rewardsEarned || 0).toFixed(4)} LOB
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {stake.isLocked === true ? 'Locked' : 'Flexible'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {stake.isActive !== false && parseFloat(stake.stakedAmount || stake.amount || 0) > 0 ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                                  Ended
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination for All Stakes */}
              {isConnected && allStakesFiltered.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {(allStakesPage - 1) * rowsPerPage + 1} to {Math.min(allStakesPage * rowsPerPage, allStakesFiltered.length)} of {allStakesFiltered.length} stakes
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setAllStakesPage(prev => Math.max(1, prev - 1))}
                      disabled={allStakesPage === 1}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Page {allStakesPage} of {allStakesTotalPages}
                    </span>
                    <button
                      onClick={() => setAllStakesPage(prev => Math.min(allStakesTotalPages, prev + 1))}
                      disabled={allStakesPage >= allStakesTotalPages}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Staking Tab */}
        {activeTab === 'staking' && (
          <div className="space-y-6">
            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Total Staked
                </h3>
                <p className="text-2xl font-bold text-heading">
                  {parseFloat(userStats.totalStaked || 0).toLocaleString()} LOB
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Total Locked
                </h3>
                <p className="text-2xl font-bold text-heading">
                  {parseFloat(userStats.totalLocked || 0).toLocaleString()} LOB
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Claimable Rewards
                </h3>
                <p className="text-2xl font-bold text-heading">
                  {parseFloat(userStats.claimableRewards || 0).toFixed(4)} LOB
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex items-center justify-center">
                <button
                  onClick={() => handleClaimReward()}
                  disabled={isClaiming || parseFloat(userStats.claimableRewards || 0) <= 0}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isClaiming ? 'Claiming...' : 'Claim All Rewards'}
                </button>
              </div>
            </div>

            {/* User Stakes Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-heading">My Stakes</h2>
                  <div className="flex items-center space-x-2">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="ended">Ended</option>
                    </select>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="normal">Normal</option>
                      <option value="locked">Locked</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Staked Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Rewards Earned
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {myStakesFiltered.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                          No stakes found
                        </td>
                      </tr>
                    ) : (
                      myStakesPaginated.map((stake) => {
                        const locked = isStakeLocked(stake);
                        const timeUntilUnlock = getTimeUntilUnlock(stake);
                        
                        return (
                          <tr key={stake.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {parseFloat(stake.amount || 0).toLocaleString()} LOB
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {parseFloat(stake.rewardsEarned || 0).toFixed(4)} LOB
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {stake.isLocked && stake.unlockTime ? (
                                (() => {
                                  const unlockTimeNum = parseInt(stake.unlockTime);
                                  if (unlockTimeNum > 0) {
                                    const now = Math.floor(Date.now() / 1000);
                                    const unlockTimestamp = unlockTimeNum;
                                    if (unlockTimestamp > now) {
                                      const daysLeft = Math.ceil((unlockTimestamp - now) / (24 * 60 * 60));
                                      return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
                                    } else {
                                      return 'Unlocked';
                                    }
                                  }
                                  return 'Locked';
                                })()
                              ) : (
                                'Flexible'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {stake.isActive ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                                  Ended
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                              <button
                                onClick={() => handleUnstakeClick(stake.id, stake.amount)}
                                disabled={locked || !stake.isActive || isStaking}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                              >
                                {locked ? 'Locked' : 'Unstake'}
                              </button>
                              <button
                                onClick={() => handleClaimReward(stake.id)}
                                disabled={isClaiming || parseFloat(stake.rewardsEarned || 0) <= 0}
                                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                              >
                                {isClaiming && claimingStakeId === stake.id ? 'Claiming...' : 'Claim'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination for My Stakes */}
              {myStakesFiltered.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {(myStakesPage - 1) * rowsPerPage + 1} to {Math.min(myStakesPage * rowsPerPage, myStakesFiltered.length)} of {myStakesFiltered.length} stakes
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setMyStakesPage(prev => Math.max(1, prev - 1))}
                      disabled={myStakesPage === 1}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Page {myStakesPage} of {myStakesTotalPages}
                    </span>
                    <button
                      onClick={() => setMyStakesPage(prev => Math.min(myStakesTotalPages, prev + 1))}
                      disabled={myStakesPage >= myStakesTotalPages}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Unstake Confirmation Modal */}
      {showUnstakeModal && (
        <div className="modal-overlay" style={{ zIndex: 40 }}>
          <div className="modal-content" style={{ zIndex: 50, maxWidth: '500px' }}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-heading text-heading dark:text-white">
                Confirm Unstake
              </h2>
              <button
                onClick={() => {
                  setShowUnstakeModal(false);
                  setUnstakeStakeId(null);
                  setUnstakeAmount(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to unstake your tokens?
              </p>
              {unstakeAmount && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Amount to Unstake:</p>
                  <p className="text-lg font-semibold text-heading">
                    {parseFloat(unstakeAmount).toLocaleString()} LOB
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                This action cannot be undone. You will receive your staked tokens back to your wallet.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowUnstakeModal(false);
                    setUnstakeStakeId(null);
                    setUnstakeAmount(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnstake}
                  disabled={isStaking}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isStaking ? 'Unstaking...' : 'Confirm Unstake'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingPage;
